import React, { useContext, useEffect, useMemo, useState } from "react";
import { TextField } from "@mui/material";
import { useMaterialReactTable, MaterialReactTable } from "material-react-table";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { ClaimsContext } from "../../../services/ClaimsService";
import { saveUKETSOverrideValues } from "../../../services/UKETSAerService";
import { Button } from "abs-ui-components";
import { useSelector } from "react-redux";
const VERIFICATION_COMPLETED = "Verification Completed";

const FuelConsumptionTab = ({ fuelConsumptions, onRowsChange, reportMstId, ukEtsCompanyLevelReportId, isReportEditable, status }) => {
	const { userHasClaim } = useContext(ClaimsContext);
	const rows = fuelConsumptions;
	const setRows = onRowsChange;
	const [drafts, setDrafts] = useState({});
	const [errors, setErrors] = useState({});
	const [isSaving, setIsSaving] = useState(false);
	useEffect(() => {
		setDrafts({});
		setErrors({});
	}, [fuelConsumptions]);

	const isVerificationCompleted = status === VERIFICATION_COMPLETED;
	const hasDirtyFields = Object.keys(drafts).length > 0;
	const hasErrors = Object.values(errors).some(Boolean);
	const user = useSelector((state) => state.userInfo.userData);
	const currentUserRole = user ? user?.roles : null;
	const ALLOWED_ROLES = ["ER_SUPERADMIN", "ER_REVIEWER", "ER_VERIFIER"];
	const hasAllowedRole = currentUserRole.some((role) => ALLOWED_ROLES.includes(role));
    const show = hasAllowedRole; 
	const isSaveDisabled = isVerificationCompleted || !hasAllowedRole || !hasDirtyFields || hasErrors || isSaving;

	const handleCellChange = (row, newValue) => {
		const fuelId = row.original.fuelId;
		const fuelConsumption = row.original.fuelConsumption;

		setDrafts((prev) => ({
			...prev,
			[fuelId]: newValue,
		}));
		if (newValue.startsWith("-")) {
			setErrors((prev) => ({
				...prev,
				[fuelId]: "Value must be a non-negative number.",
			}));
			return;
		}

		if (newValue !== "" && !/^\d+(\.\d+)?$/.test(newValue)) {
			setErrors((prev) => ({
				...prev,
				[fuelId]: "Invalid number. Please enter a valid number.",
			}));
			return;
		}

		const parsed = newValue === "" ? null : Number(newValue);

		if (newValue !== "" && fuelConsumption !== null && fuelConsumption !== undefined && parsed > Number(fuelConsumption)) {
			setErrors((prev) => ({
				...prev,
				[fuelId]: `Value cannot exceed ${fuelConsumption}.`,
			}));
			return;
		}
		//decimal validation should be up to 5 decimals
		if(newValue !== "" && !/^\d+(\.\d{1,5})?$/.test(newValue)) {
			setErrors((prev) => ({
				...prev,
				[fuelId]: "Value can have up to 5 decimal places.",
			}));
			return;
		}

		setErrors((prev) => {
			const next = { ...prev };
			delete next[fuelId];
			return next;
		});
	};

	const handleSaveAll = async () => {
		if (isSaveDisabled) return;

		const invalidEntry = Object.entries(drafts).find(([, value]) => value !== "" && !/^\d+(\.\d+)?$/.test(String(value)));

		if (invalidEntry) {
			const [fuelId] = invalidEntry;

			setErrors((prev) => ({
				...prev,
				[fuelId]: "Invalid number. Please enter a valid number before saving.",
			}));

			emitToasterMsg("error", "Invalid Value", "Invalid number cannot be saved. Please correct the highlighted field.");

			return;
		}
		setIsSaving(true);
		try {
			const fuelCodeUserValue = {};

			rows.forEach((r) => {
				if (r.fuelId === null || r.fuelId === undefined) return;

				const hasDraft = Object.prototype.hasOwnProperty.call(drafts, r.fuelId);

				if (hasDraft) {
					const draftVal = drafts[r.fuelId];
					fuelCodeUserValue[r.fuelId] = draftVal === "" ? "" : String(parseFloat(draftVal));
				} else if (r.eligibleForEmission !== null && r.eligibleForEmission !== undefined && r.eligibleForEmission !== "") {
					fuelCodeUserValue[r.fuelId] = String(r.eligibleForEmission);
				}
			});

			const payload = {
				ukEtsComapnyLevelReportId: Number(ukEtsCompanyLevelReportId),
				reportMstId: Number(reportMstId),
				fuelCodeUserValue,
			};

			const result = await saveUKETSOverrideValues(payload);

			if (result?.isSuccess) {
				setRows((prev) =>
					prev.map((r) => {
						if (!Object.prototype.hasOwnProperty.call(drafts, r.fuelId)) return r;
						const draftVal = drafts[r.fuelId];
						return {
							...r,
							eligibleForEmission: draftVal === "" ? null : parseFloat(draftVal),
						};
					})
				);
				setDrafts({});
				setErrors({});
				emitToasterMsg("success", "Success", "Emission reduction claim values saved successfully.");
			} else {
				emitToasterMsg("error", "Save Failed", result?.errorMessage || "Unable to save emission reduction claim values.");
			}
		} catch (err) {
			emitToasterMsg("error", "Error", err.message);
		} finally {
			setIsSaving(false);
		}
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: "sNo",
				header: "S.No.",
				size: 80,
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				Cell: ({ row }) => row.index + 1,
			},
			{
				accessorKey: "fuelId",
				header: "Fuel Type ID",
				minSize: 80,
				grow: true,
				enableColumnActions: false,
				enableColumnFilters: false,
			},
			{
				accessorKey: "fuelName",
				header: "Fuel Name",
				size: 120,
				enableColumnActions: false,
				enableColumnFilters: false,
			},
			{
				accessorKey: "ukEtsEfCo2",
				header: "UK ETS EF CO2",
				size: 130,
				enableColumnActions: false,
				enableColumnFilters: false,
			},
			{
				accessorKey: "ukEtsEfCh4",
				header: "UK ETS EF CH4",
				size: 130,
				enableColumnActions: false,
				enableColumnFilters: false,
			},
			{
				accessorKey: "ukEtsEfN2o",
				header: "UK ETS EF N2O",
				size: 130,
				enableColumnActions: false,
				enableColumnFilters: false,
			},
			{
				accessorKey: "fuelConsumption",
				header: "Fuel Consumption (MT)",
				size: 160,
				enableColumnActions: false,
				enableColumnFilters: false,
			},
			{
				accessorKey: "eligibleForEmission",
				header: "Eligible for Emission Reduction Claim – Para 37",
				size: 260,
				enableColumnActions: false,
				enableColumnFilters: false,

				Cell: ({ row }) => {
					const { fuelId, isEditable, eligibleForEmission } = row.original;

					// Non-editable fuel type
					if (!isEditable) {
						return <span>Not Applicable</span>;
					}

					const fieldEditable = isEditable && isReportEditable && show;

					// Controlled value: draft takes priority over committed value
					const hasDraft = Object.prototype.hasOwnProperty.call(drafts, fuelId);
					const displayValue = hasDraft
						? drafts[fuelId]
						: eligibleForEmission !== null && eligibleForEmission !== undefined
							? String(eligibleForEmission)
							: "";

					const errorMsg = errors[fuelId] || "";

					return (
						<ClaimInputCell
							value={displayValue}
							disabled={!fieldEditable || isSaving}
							readOnly={!show}
							onChange={(val) => handleCellChange(row, val)}
							errorMsg={errorMsg}
						/>
					);
				},
			},
		],
		[isReportEditable, drafts, errors, isSaving]
	);

	const table = useMaterialReactTable({
		columns,
		data: rows,

		enableStickyHeader: true,
		enablePagination: false,
		enableColumnPinning: true,
		enableSorting: false,
		enableColumnActions: false,
		enableEditing: false,
		enableRowSelection: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnFilters: false,
		enableGlobalFilter: false,
		enableDensityToggle: false,
		enableFullScreenToggle: false,
		enableHiding: false,
		enableShowHideColumns: false,
		
		initialState: {
			density: "compact",
			columnPinning: {
				left: ["sNo", "fuelId", "fuelName"], 
			},
		},
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
			},
		},

		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",
				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},

		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},

		muiTableContainerProps: {
			sx: {
				maxHeight: "450px",
			},
		},

		renderTopToolbarCustomActions: () => (
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					width: "100%",
					padding: "0 8px",
				}}
			>
				<span
					style={{
						fontWeight: "500",
						fontSize: "16px",
						color: "#002a4e",
					}}
				>
					Fuel Consumption by Fuel Type
				</span>
			</div>
		),
	});

	return (
		<div className="p-2">
			<MaterialReactTable table={table} />

			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					marginTop: "10px",
					marginRight: "8px",
				}}
			>
				{show && (
					<Button
						disabled={isSaveDisabled}
						onClick={handleSaveAll}
						sx={{
							backgroundColor: isVerificationCompleted ? undefined : "#042a4c",
							"&:hover": {
								backgroundColor: isVerificationCompleted ? undefined : "#063a6a",
							},
							textTransform: "none",
							fontSize: "13px",
							padding: "4px 16px",
						}}
					>
						{isSaving ? "Saving..." : "Save"}
					</Button>
				)}
			</div>
		</div>
	);
};

// Controlled input cell — value and onChange come from the parent
const ClaimInputCell = ({ value, disabled, readOnly, onChange, errorMsg }) => {
	return (
		<div>
			<TextField
				value={value}
				disabled={disabled}
				size="small"
				variant="outlined"
				type="text"
				inputProps={{
					inputMode: "decimal",
					readOnly: readOnly,
				}}
				sx={{
					width: "120px",
					"& .MuiOutlinedInput-root": {
						"& fieldset": {
							borderColor: errorMsg ? "#d32f2f" : "#ccc",
						},
						"&:hover fieldset": {
							borderColor: errorMsg ? "#d32f2f" : "#042a4c",
						},
					},
				}}
				onChange={(e) => {
					if (readOnly) return;
					const newValue = e.target.value;
					onChange(newValue);
				}}
				onKeyDown={(e) => {
					if (readOnly) {
						e.preventDefault();
						return;
					}
					if (["e", "E", "+"].includes(e.key)) {
						e.preventDefault();
					}
				}}
			/>
			{errorMsg && (
				<div
					style={{
						color: "#d32f2f",
						fontSize: "11px",
						marginTop: "2px",
						maxWidth: "120px",
						lineHeight: "1.3",
					}}
				>
					{errorMsg}
				</div>
			)}
		</div>
	);
};

export default FuelConsumptionTab;
